import Sources from '@/data/sources';
import Metric from '@/types/metric';
import Months from '@/types/months';
import * as Api from '@/api';
import moment from 'moment';
import legendItem from '@/types/legendItem';
import LegendItem from '@/types/legendItem';

const sourceUrl = process.env.VUE_APP_OpenData_Base_Dataset_URL;

export function GetMetric(topic:any) :any{
    if (typeof topic !== 'string') {
        return {};
    }
    return Sources.filter((x:Metric) => x.topic == topic)[0];
}

export  async function GetMetricData(topic:any) :Promise<any>{
    if (typeof topic !== 'string') {
        return [];
    }
    const metric: Metric = Sources.filter((x:Metric) => x.topic == topic)[0];

    let data: any = await Api.GetMetricData(metric.code);

    switch(metric.rate) {
        case 'month':
            data = data.sort((a:any, b:any) => {
                const aDate = GetDateFromMonthYear(a[metric.dateProp]);
                const bDate = GetDateFromMonthYear(b[metric.dateProp]);
                return (aDate < bDate) ? -1 : (aDate > bDate) ? 1 : 0;
            }) 
        break;
        case 'quarter':
            data = data.sort((a:any, b:any) => {
                const aDate = GetDateFromQuarter(a[metric.dateProp]);
                const bDate = GetDateFromQuarter(b[metric.dateProp]);
                return (aDate < bDate) ? -1 : (aDate > bDate) ? 1 : 0;
            }) 
        break;
        case 'year': 
        data = data.sort((a:any, b:any) => {
            const aDate = GetDateFromYear(a[metric.dateProp]);
            const bDate = GetDateFromYear(b[metric.dateProp]);
            return (aDate < bDate) ? -1 : (aDate > bDate) ? 1 : 0;
        })
        break;
        default:
            // null, so do nothing and trust what we back is in the correct order
        break;
    }


    return data;
}

export async function GetSpotlight() {
    const spotlight = await Api.GetSpotlight();
    const metric = Sources.filter((x:Metric) => x.code == spotlight[0].Metric.OpenDataID)[0];
    return metric
}

export function NumberWithCommas(x:any) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function GetDateFromMonthYear(value:string) {
    
    const strSplit = value.split('-');
    const month = Months.filter( x => x.month === strSplit[0].toLowerCase())[0].number;
    const year = Number(strSplit[1]) > 60 ? `19${strSplit[1]}` : `20${strSplit[1]}`;
    return new Date(`${year}-${(month < 10)? `0${month}` : month}-01T12:00:00.000`);
}

export function GetDateFromQuarter(value:string) {
    const split = value.split(''); // i.e. Q1-99 = 01/15/1999   Q4-21 = 1-/15/2021 
    const year = (split[3] == '9')? `199${split[4]}`: `20${split[3]}${split[4]}`;
    switch(split[1]){
        case '1':
            // Q1-xx
            return new Date(`${year}-01-15T12:00:00.000`)
        case '2':
            // Q2-xx
            return new Date(`${year}-04-15T12:00:00.000`);
        break;
        case '3':
            // Q3-xx
            return new Date(`${year}-07-15T12:00:00.000`);
        case '4':
        default:
            // Q4-xx
            return new Date(`${year}-10-15T12:00:00.000`);
    }
}

export function GetDateFromYear(value:string) {
    return new Date(`${value}-01-15T12:00:00.000`);
}

export function SummaryTitleByMonth(date:any) {
    const dateText = moment(date).format('MMMM YYYY');
    return `Summary for ${dateText}`;
}

export function SummaryTitleByQuarter(_date:any) {
    const date = GetDateFromQuarter(_date);
    const quarter = _date.substring(0,2);
    return `Summary for ${quarter} ${date.getFullYear()}`;
}

export function SummaryTitleByYear(date:any) {
    const dateText = moment(date).format('YYYY');
    return `Summary for ${dateText}`;
}

export function GetHighlight(data:any[]) {
    let highlightLabel = '';
    const results = data.filter((x:any) => { 
        if(typeof x.highlight !== 'undefined') {
            highlightLabel = 'highlight';
            return x;
        }
        if(typeof x.highlights !== 'undefined') {
            highlightLabel = 'highlights';
            return x;
        }
    });

    // The last entry should contain the correct highlight
    return (results.length > 0) ? results[results.length - 1][highlightLabel] : "";
}

export function ChangeOverTime(data:any[], interval:number, label:string, asPercent:boolean) {
    const target = data.length - 1 - interval;
    let lastRec =  Number(data[data.length - 1][label]);
    let compare  = Number(data[target][label]);
    let variance = 1;


    /* OK this is a quirk of JS and doing basic subtraction of floats. 
     Example 56.3 - 56.1 =  0.19999999999999574
        We want 0.2, and don't want to round the number 
    */

    // Start be getting how many decimal places we need
    const lastRecStr = lastRec.toString().split('.');
    const compareStr = compare.toString().split('.');
    let lastRecLength = (lastRecStr.length > 1)? lastRecStr[1].length : 0;
    let compareLength = (compareStr.length > 1)? compareStr[1].length : 0;

    // OK we have a really long decimal point. This is probably from a percent data point and should be  rounded for some calculations
    // ** using 4 as a cut off thinking i.e. 0.1456 will be 14.56% and can be nicely rounded to 14.7%
    if (lastRecLength > 4 || compareLength > 4) {
        lastRec = Number(lastRec.toFixed(4));
        compare = Number(compare.toFixed(4));
        lastRecLength = 4;
        compareLength = 4;
    }

    // Update the variance 
    if (lastRecLength > compareLength) {
        variance = Math.pow(10, lastRecLength);
    } else if (lastRecLength < compareLength || (lastRecLength == compareLength && compareLength > 0)) {
        variance = Math.pow(10, compareLength);
    } 

    // move the decimal point so we have a whole number
    lastRec = lastRec * variance;
    compare = compare * variance;

    // Do our simple math and return the difference to the decimal form
    const difference = Number(lastRec - compare) / variance;

    
    // Last return the number as a string or convert to a percentage
    if (asPercent) {
        return (difference / (compare / variance) * 100).toFixed(1).toString();
    } else {
        return difference.toString();
    }
    
}

export function SourceURL(metric:any) {
    return `${sourceUrl}${metric.code}/data`
}


export function GetLegendItems(series:any[], data:any, dataProp:string, ): Array<LegendItem> {

    if (data == null) return [];

    const legend :Array<legendItem> = [];
    const values: Array<number> = data.map((x:any) => x[dataProp]);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const increment = Number(((max - min) / series.length).toFixed(1));
    let val = min;

    series.forEach((item:any, index:number) => {
        const legendItem = new LegendItem();
        legendItem.iconCSS.backgroundColor = `rgba(${item.fill.join(',')})`;

        if (index == 0) { //first item
            legendItem.min = 0;
            legendItem.max = (val + increment);
            legendItem.text = `0.0% - ${legendItem.max.toFixed(1)}%`;
            val =  Number((val + increment + 0.1).toFixed(1));
        } else if (index == (series.length - 1)) { // last item
            legendItem.min = Number((val).toFixed(1));
            legendItem.max = max;
            legendItem.text = `${legendItem.min.toFixed(1)}% - ${legendItem.max.toFixed(1)}%`;
        } else { // all other items
            legendItem.min = Number((val).toFixed(1));
            legendItem.max = val + increment;
            legendItem.text = `${legendItem.min.toFixed(1)}% - ${legendItem.max.toFixed(1)}%`;
            val =  Number((val + increment + 0.1).toFixed(1));
        }

        legend.push(legendItem);
    })

    return legend;
}

export function GetMoneyFormat(value:any) {
    
    const prefix = ((Number(value) < 0) ? '-' : '');
    const x = `${prefix}$${Math.abs(value).toFixed(2)}`;
    return NumberWithCommas(x);
}